import React, { ReactElement } from 'react';
import { Box, HStack, Text, VStack, Image, Grid, Button } from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import placeholder from 'assets/common/placeholder.png';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { VideoBox } from 'components/common/VideoBox/VideoBox';
import { AddIcon, DeleteIcon, RestartIcon } from 'assets/icons/v2';
import { NHVideoBlockProps } from '../NHVideoBlock';
import { BlockInput } from '../../../common/BlockInput';

type Props = NHVideoBlockProps & {
  onDelete?: () => void;
};

export const TeacherNHVideoBlock = ({ block, editor, onDelete }: Props): ReactElement => {
  const modalService = useModalService();

  const handleVideoClick = (): void => {
    modalService.show(ModalVariant.WORKSHEET_VIDEO, {
      onSelect: (data: {
        videoUrl: string;
        title: string;
        description?: string;
        thumbnailUrl?: string;
        qrCodeUrl?: string;
      }) => {
        editor.updateBlock(block, {
          props: {
            videoUrl: data.videoUrl,
            title: block.props.title || data.title,
            description: block.props.description || data.description,
            thumbnailUrl: data.thumbnailUrl,
            qrCodeUrl: data.qrCodeUrl,
          },
        });
      },
    });
  };

  return (
    <Box
      p="l"
      border="default"
      borderColor="border.accent"
      borderRadius="l"
      width="100%"
      contentEditable={false}
      bg="grey.white"
    >
      <VStack gap="l" width="100%">
        <HStack justifyContent="space-between" alignItems="center" width="100%">
          <Text textStyle="body.m.medium" color="text.primary" cursor="default">
            Video
          </Text>
          {onDelete && (
            <NovaIconButton
              aria-label="Video löschen"
              variant="primary"
              size="m"
              onClick={onDelete}
              boxSize="18px"
              fontSize="18px"
              lineHeight="0"
              minWidth="0px"
              _hover={{
                '& svg': {
                  color: 'red.500',
                },
              }}
              icon={DeleteIcon}
              iconProps={{ fill: 'transparent', color: 'icon.quaternary' }}
            />
          )}
        </HStack>
        <Grid templateColumns="1fr 1fr" gap="l">
          <Box
            alignContent="center"
            borderRadius="l"
            overflow="hidden"
            width="100%"
            position="relative"
          >
            {block.props.videoUrl ? (
              <VideoBox
                videoUrl={block.props.videoUrl}
                videoDescription={block.props.description}
                isEmbedded
                thumbnailUrl={block.props.thumbnailUrl}
              />
            ) : (
              <Image
                src={placeholder}
                alt="Video Thumbnail"
                borderRadius="l"
                objectFit="cover"
                onClick={handleVideoClick}
                cursor="pointer"
                transition="transform 0.2s"
              />
            )}
          </Box>
          <VStack gap="l" width="100%" alignItems="flex-start">
            <BlockInput
              label="Titel"
              name="title"
              value={block.props.title}
              onChange={(e) => {
                editor.updateBlock(block, {
                  props: {
                    title: e.target.value,
                  },
                });
              }}
              placeholder="Titel eingeben..."
            />
            <BlockInput
              label="Beschreibung"
              name="description"
              value={block.props.description}
              onChange={(e) => {
                editor.updateBlock(block, {
                  props: {
                    description: e.target.value,
                  },
                });
              }}
              placeholder="Beschreibung eingeben..."
            />
            {block.props.videoUrl ? (
              <ChangeVideoButton onClick={handleVideoClick} />
            ) : (
              <AddVideoButton onClick={handleVideoClick} />
            )}
          </VStack>
        </Grid>
      </VStack>
    </Box>
  );
};

const AddVideoButton = ({ onClick }: { onClick: () => void }): ReactElement => {
  return (
    <Button
      variant="tertiaryAction"
      alignSelf="flex-start"
      onClick={onClick}
      textStyle="body.s.regular"
    >
      <NovaIcon icon={AddIcon} size="s" aria-hidden />
      Video hinzufügen
    </Button>
  );
};

const ChangeVideoButton = ({ onClick }: { onClick: () => void }): ReactElement => {
  return (
    <Button
      variant="tertiaryAction"
      alignSelf="flex-start"
      onClick={onClick}
      textStyle="body.s.regular"
    >
      <NovaIcon icon={RestartIcon} size="s" aria-hidden />
      Video ändern
    </Button>
  );
};
